import axios from 'axios';
import { getRouter } from '@/router';
import { addApiErrorToast } from '@/shared/composables/useToast.js';
import { useAuthStore } from '@/features/auth/stores/authStore.js';

const baseURL = import.meta.env.DEV
  ? ''
  : import.meta.env.VITE_API_BASE_URL || '';

export function clearAuthData() {
  const authStore = useAuthStore();
  authStore.clearAccessToken();
  localStorage.removeItem('isLoggedIn');
}

const axiosInstance = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let refreshPromise = null;

const AUTH_URLS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/logout'];

function isAuthUrl(url) {
  return AUTH_URLS.some((authUrl) => url?.includes(authUrl));
}

function redirectToLogin() {
  const router = getRouter();
  if (router) {
    router.push('/login');
  } else {
    window.location.href = '/login';
  }
}

export async function refreshAccessToken() {
  const authStore = useAuthStore();
  // refreshToken 由 HttpOnly cookie 自動帶，不需傳入 body
  const response = await axios.post(
    `${baseURL}/api/auth/refresh`,
    {},
    {
      withCredentials: true,
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' },
    },
  );
  const { accessToken: newToken } = response.data.data;
  authStore.setAccessToken(newToken);
  return newToken;
}

// ==================== Request Interceptor ====================

axiosInstance.interceptors.request.use(
  (config) => {
    if (isAuthUrl(config.url)) return config;

    const { accessToken } = useAuthStore();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ==================== Response Interceptor ====================

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401) {
      const { status, data, config: cfg } = error.response ?? {};
      if (!cfg?.suppressErrorToast) {
        const message =
          data?.error || data?.message || error.message || 'Unknown error';
        addApiErrorToast(status, cfg?.method, cfg?.url, message);
      }
      return Promise.reject(error);
    }

    if (isAuthUrl(originalRequest?.url)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      clearAuthData();
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch {
      clearAuthData();
      redirectToLogin();
      return Promise.reject(error);
    }
  },
);

export default axiosInstance;
